import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import cn from 'classnames'
import { Flex, SVGIcon } from 'shared/ui'

import ReplyStateIndicator from 'components/ReplyStateIndicator'

// NOTE (jscheel): Currently circumventing the performance-enhanced version in
// ./AssignmentDropdown.jsx, as it doesn't actually work right now. We can
// re-address after launch (or whenever someone reads this note three years
// from now), but going with this for the time being.
import AssignmentBadge from 'components/App/DesktopView/shared/AssignmentDropdown/Badge'
import AssignmentDropdown from 'components/App/DesktopView/shared/AssignmentDropdown/ReplyPage'
// END NOTE
import EmojiPicker from 'components/App/DesktopView/shared/EmojiPicker'

import AttachmentIcon from '../../shared/AttachmentIcon'
import AlignedToolbar from '../../shared/AlignedToolbar'

import Button from './Button'
import ToolbarIcon from './ToolbarIcon'

const flexStyle = css`
  flex: 0 0 auto;
`

const notSendableReasonStyle = theme => css`
  margin-right: 24px;
  font-style: italic;
  color: ${theme.color.primary.negative};
`

const notSendableReasonLargeScreenStyle = css`
  @media (max-width: 1249px) {
    display: none;
  }
`

const notSendableReasonSmallScreenStyle = css`
  text-align: right;
  margin-right: 0;
  @media (min-width: 1250px) {
    display: none;
  }
`

const breakFlexNewRowStyle = css`
  flex-basis: 100%;
  height: 0;
  @media (min-width: 1250px) {
    display: none;
  }
`

const ToolbarContainer = styled('div')`
  margin-left: -16px;
  margin-right: -16px;
  overflow: hidden;
  transition: all 150ms ease-in-out;
  height: 0;
  background-color: white;
  border-bottom: 0px solid ${props => props.theme.color.monochrome.light};
  ${({ visible }) => {
    return (
      visible &&
      css`
        height: 34px;
        border-bottom-width: 1px;
      `
    )
  }} & > #email-editor-toolbar {
    padding: 4px 4px 4px 0;
    display: block;
    height: auto;
  }
  & > #email-editor-toolbar > .mce-panel {
    display: block !important;
  }
`

const HoverSVGIcon = styled(SVGIcon)`
  color: ${props => props.theme.color.monochrome.mediumDark};
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.color.primary.brand};
  }
`

class Footer extends PureComponent {
  static propTypes = {
    actionLabel: PropTypes.string,
    // draft: PropTypes.object.isRequired,
    draftAssignedGroupId: PropTypes.string,
    draftAssignment: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    isDraftSendable: PropTypes.bool.isRequired,
    /** Is ticket state open  */
    isOpen: PropTypes.bool.isRequired,
    /* Callback called before any send. If returns false, stops sending */
    onDeleteDraft: PropTypes.func.isRequired,
    onToolbarToggle: PropTypes.func.isRequired,
    sending: PropTypes.bool,
    ticketId: PropTypes.string.isRequired,
  }

  static defaultProps = {
    actionLabel: 'Reply',
    draftAssignedGroupId: undefined,
    draftAssignment: undefined,
    editorRef: undefined,
    sending: false,
    showAttachmentIcon: false,
    showToolbar: false,
    showToolbarIcon: false,
  }

  render() {
    const {
      actionLabel,
      className,
      draftAssignedGroupId,
      draftAssignment,
      hideCollisionIndicator,
      isDraftSendable,
      notSendableReason,
      isForwarding,
      isOpen,
      onDeleteDraft,
      onSendOpenClick,
      onSendClosedClick,
      onSnoozeOptionClick,
      onToolbarToggle,
      sending,
      showAttachmentIcon,
      showToolbar,
      showToolbarIcon,
      ticketId,
    } = this.props

    return (
      <Flex css={flexStyle} className="grui flex-col">
        <ToolbarContainer className="grui mr-3" visible={showToolbar}>
          <div id="email-editor-toolbar" />
        </ToolbarContainer>

        <Flex
          className={cn(
            className,
            { forwarding: isForwarding },
            'grui flex-wrap'
          )}
        >
          <AlignedToolbar
            css={css`
              & > * + * {
                margin-left: 4px;
              }
            `}
          >
            {showToolbarIcon && (
              <React.Fragment>
                <div className="grui mr-3" css={{ position: 'relative' }}>
                  <ToolbarIcon
                    onClick={onToolbarToggle}
                    showToolbar={showToolbar}
                    upward
                  />
                </div>
              </React.Fragment>
            )}
            {showAttachmentIcon && (
              <div className="grui mr-3">
                <AttachmentIcon icon={<HoverSVGIcon name="updatedAttach" />} />
              </div>
            )}
            <div className="grui mr-3">
              <HoverSVGIcon
                data-test-id="DraftDeleteButton"
                name="updatedDelete"
                onClick={onDeleteDraft}
                withTooltip={{ tooltip: 'Delete', position: 'top' }}
              />
            </div>
            <EmojiPicker />
          </AlignedToolbar>

          <Flex className="grui justify-end flex-1-0-auto items-center">
            {!isDraftSendable &&
              notSendableReason && (
                <span
                  css={[
                    notSendableReasonStyle,
                    notSendableReasonLargeScreenStyle,
                  ]}
                  className="not-sendable-reason"
                >
                  {notSendableReason}
                </span>
              )}
            <div className="grui mr-3">
              <AssignmentDropdown
                direction="left"
                drilledDownGroupId={draftAssignedGroupId}
                key="assignment"
                trigger={
                  <div>
                    <AssignmentBadge
                      assignment={draftAssignment}
                      // bgColor="white"
                      size="small"
                      iconSize={24}
                      tooltipPosition="top"
                    />
                  </div>
                }
                upward
              />
            </div>
            <div className="sendButtonContainer">
              <Button
                actionLabel={actionLabel}
                disabled={!isDraftSendable || sending}
                isOpen={isOpen}
                onSendOpenClick={onSendOpenClick}
                onSendClosedClick={onSendClosedClick}
                onSnoozeOptionClick={onSnoozeOptionClick}
              />
              {!hideCollisionIndicator && (
                <ReplyStateIndicator
                  className={'collisionIndicator'}
                  hideDraftIndicator
                  conversationId={ticketId}
                />
              )}
            </div>
          </Flex>
          {!isDraftSendable &&
            notSendableReason && (
              <React.Fragment>
                <div css={breakFlexNewRowStyle} />
                <span
                  css={[
                    notSendableReasonStyle,
                    notSendableReasonSmallScreenStyle,
                  ]}
                  className="grui not-sendable-reason flex-1-0-auto"
                >
                  {notSendableReason}
                </span>
              </React.Fragment>
            )}
        </Flex>
      </Flex>
    )
  }
}

export default styled(Footer)`
  align-items: center;
  background-color: white;
  flex: 0 0 auto;
  padding-top: 8px;

  &.forwarding {
    background-color: ${props => props.theme.color.secondary.positive};
  }

  .sendButtonContainer {
    margin-left: ${props => props.theme.spacing['4']};
    position: relative;
  }

  .collisionIndicator {
    position: absolute;
    top: -9px;
    right: -9px;
  }
`
